import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Play, Loader2, X, Maximize, Minimize } from 'lucide-react';
import { propertyData } from '../../data/mock';
import { setGlobalActivePlayer } from './HeroSection';

const VirtualTourSection = () => {
  const [showVideo, setShowVideo] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef(null);

  // YouTube video ID for drone footage
  const youtubeId = propertyData.droneYouTubeId;

  // Handle fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Play video - shows YouTube embed
  const playVideo = useCallback(() => {
    setIsLoading(true);
    setShowVideo(true);
    
    // Register as active player - will pause other videos
    setGlobalActivePlayer({
      pause: () => {
        setShowVideo(false);
        setIsLoading(false);
      }
    });
  }, []);

  // Close video
  const closeVideo = useCallback(() => {
    setShowVideo(false);
    setIsLoading(false);
  }, []);

  // Handle iframe load
  const handleIframeLoad = useCallback(() => {
    setIsLoading(false);
  }, []);

  // Toggle fullscreen
  const toggleFullscreen = useCallback(() => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  }, []);

  return (
    <section id="virtual-tour" className="py-24 bg-gradient-to-b from-[#0a0c10] to-[#0d1117]" data-testid="virtual-tour-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <span className="inline-block px-4 py-1.5 bg-amber-500/10 border border-amber-500/30 rounded-full text-amber-400 text-sm font-medium tracking-wide mb-4">
            Aerial Experience
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-white mb-4">
            DRONE PROPERTY TOUR
          </h2>
          <p className="text-lg text-white/70 font-light">
            Experience the estate from above with our aerial drone footage
          </p>
        </div>

        {/* Video Container */}
        <div 
          ref={containerRef}
          className="relative max-w-5xl mx-auto rounded-2xl overflow-hidden shadow-2xl shadow-black/50"
        >
          <div className="relative aspect-video bg-black">
            {/* Poster/Preview - Visible when not playing */}
            {!showVideo && (
              <div className="absolute inset-0">
                <img
                  src={propertyData.images.aerial}
                  alt="Drone Tour Preview"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/40" />
                
                {/* Play Button Overlay */}
                <div 
                  className="absolute inset-0 flex items-center justify-center cursor-pointer transition-all hover:bg-black/50"
                  onClick={playVideo}
                >
                  <div className="flex flex-col items-center">
                    <button
                      className="w-24 h-24 rounded-full bg-amber-500/90 backdrop-blur-sm flex items-center justify-center text-white shadow-2xl shadow-amber-500/40 hover:bg-amber-500 hover:scale-110 transition-all duration-300"
                      data-testid="virtual-tour-play-btn"
                    >
                      <Play className="w-10 h-10 ml-1" />
                    </button>
                    <span className="mt-4 text-white text-sm font-medium bg-black/60 backdrop-blur-sm rounded-full px-4 py-1.5">
                      START DRONE TOUR
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* YouTube Embed - Visible when playing */}
            {showVideo && (
              <>
                <iframe
                  className="w-full h-full"
                  src={`https://www.youtube-nocookie.com/embed/${youtubeId}?autoplay=1&rel=0&modestbranding=1&playsinline=1&origin=${window.location.origin}`}
                  title="Drone Property Tour"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  onLoad={handleIframeLoad}
                  data-testid="virtual-tour-youtube-iframe"
                />
                
                {/* Loading overlay */}
                {isLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/60 z-10 pointer-events-none">
                    <Loader2 className="w-12 h-12 text-amber-400 animate-spin" />
                  </div>
                )}

                {/* Controls */}
                <div className="absolute top-4 right-4 flex items-center space-x-2 z-20">
                  <button
                    onClick={toggleFullscreen}
                    className="w-10 h-10 rounded-full bg-black/60 backdrop-blur-md flex items-center justify-center text-white hover:bg-black/80 transition-all"
                    data-testid="virtual-tour-fullscreen-btn"
                  >
                    {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
                  </button>
                  <button
                    onClick={closeVideo}
                    className="w-10 h-10 rounded-full bg-black/60 backdrop-blur-md flex items-center justify-center text-white hover:bg-black/80 transition-all"
                    data-testid="virtual-tour-close-btn"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </>
            )}
          </div>
          
          {/* Video Badge */}
          <div className="absolute top-4 left-4 flex items-center space-x-2 bg-black/60 backdrop-blur-md rounded-full px-4 py-2 z-10">
            <div className="w-2 h-2 rounded-full bg-amber-500" />
            <span className="text-white text-sm font-medium">Drone Footage</span>
          </div>
        </div>
        
        <p className="text-center text-white/50 text-sm mt-6">
          Click play to experience an aerial view of this exceptional Central Florida estate
        </p>
      </div>
    </section>
  );
};

export default VirtualTourSection;
